import React, { memo, useEffect } from "react";

import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { logger } from "@origin-digital/reporting-client";

import { PreloaderScreen } from "@/components";
import { useRedirect } from "@/hooks/useRedirect";
import { PageWithHardStopProps } from "@/types/SolarFormProps";

import { requestingQuote } from "./content.json";
import { useLeadSubmission } from "./utils/useLeadSubmission";

type RequestingQuoteProps = Omit<PageWithHardStopProps, "backRoute"> & {
  delay: number;
};

export const RequestingQuote = memo(
  ({ delay, nextRoute, hardStopRoute }: RequestingQuoteProps) => {
    const { getValues, setValue } = useFormContext();
    const { goNextRoute } = useRedirect();
    const { submitLead } = useLeadSubmission();
    const navigate = useNavigate();

    const doRequestQuote = async () => {
      const { error } = await submitLead();
      let to: string;

      if (error) {
        logger.error("submission has an error: ", error);
        setValue("hardStop", "ServiceProviderMatching");
        if (
          error?.message === "LEAD_EXISTS_FOR_SAME_ADDRESS_WITHIN_LAST_30_DAYS"
        ) {
          setValue("hardStop", "sameAddressAgain");
        }
        to = await hardStopRoute();
      } else {
        to = await nextRoute();
      }

      goNextRoute(to);
    };

    useEffect(() => {
      if (getValues("didFinishSolarForm")) {
        navigate("/");
        return;
      }

      const timeoutId = setTimeout(() => {
        doRequestQuote();
      }, delay);

      return () => clearTimeout(timeoutId);
    });

    return (
      <PreloaderScreen
        id={requestingQuote.id}
        heading={requestingQuote.heading}
        description={requestingQuote.description}
      />
    );
  }
);
